/**
 * Activity Storage Service
 *
 * Gobodo API-based CRUD service for activity tracking.
 * All operations go through Gobodo GraphObjectsApi via gobodoActivityProvider.
 * NO localStorage fallback - requires Gobodo API connection.
 */

import { Activity, ActivityFilters, ActivityType, DateRange, RecordType } from '../types/activity';
import { gobodoActivityProvider } from './providers/gobodoActivityProvider';

// ============================================
// GOBODO API METHODS
// ============================================

/**
 * Get all activities with optional filtering
 */
const getAll = async (filters?: ActivityFilters): Promise<Activity[]> => {
  return gobodoActivityProvider.getActivities(filters);
};

/**
 * Get activities for a specific record
 */
const getByRecord = async (recordId: string, recordType: string): Promise<Activity[]> => {
  return gobodoActivityProvider.getByRecord(recordId, recordType as RecordType);
};

/**
 * Get activities by user
 */
const getByUser = async (userId: string, dateRange?: DateRange): Promise<Activity[]> => {
  return gobodoActivityProvider.getByUser(userId, dateRange?.start, dateRange?.end);
};

/**
 * Get single activity by ID
 */
const getById = async (id: string): Promise<Activity | null> => {
  return gobodoActivityProvider.getActivityById(id);
};

/**
 * Create a new activity
 */
const create = async (activity: Omit<Activity, 'id' | 'createdAt'>): Promise<Activity> => {
  return gobodoActivityProvider.createActivity({
    ...activity,
    createdAt: new Date().toISOString(),
  });
};

/**
 * Update an existing activity
 */
const update = async (id: string, updates: Partial<Activity>): Promise<Activity | null> => {
  try {
    return await gobodoActivityProvider.updateActivity(id, updates);
  } catch (error) {
    console.error('[activityStorage] Failed to update activity:', error);
    return null;
  }
};

/**
 * Delete an activity
 */
const remove = async (id: string): Promise<boolean> => {
  try {
    return await gobodoActivityProvider.deleteActivity(id);
  } catch (error) {
    console.error('[activityStorage] Failed to delete activity:', error);
    return false;
  }
};

/**
 * Analytics: Count by type for a user
 */
const getCountByType = async (userId: string, dateRange: DateRange): Promise<Record<ActivityType, number>> => {
  return gobodoActivityProvider.getCountByType(userId, dateRange.start, dateRange.end);
};

/**
 * Analytics: Daily activity count
 */
const getDailyActivityCount = async (userId: string, date: string): Promise<number> => {
  return gobodoActivityProvider.getDailyActivityCount(userId, date);
};

/**
 * Seed data - NO-OP for Gobodo API
 * This method exists for backwards compatibility but does nothing
 * as Gobodo API handles data persistence.
 */
const seedData = (): void => {
  // No-op for Gobodo API - data is managed server-side
  console.log('[activityStorage] seedData() is a no-op when using Gobodo API');
};

// ============================================
// EXPORT
// ============================================

export const activityStorage = {
  getAll,
  getByRecord,
  getByUser,
  getById,
  create,
  update,
  delete: remove,
  getCountByType,
  getDailyActivityCount,
  seedData,
};
